import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LineReveal } from "../Reveal";
import { IMAGES } from "../../lib/data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] w-full overflow-hidden flex items-end"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Stadium under floodlights"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-0 bg-[#0a0a0a]"
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" />

      <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-20 sm:pb-28 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-neutral-300"
        >
          <span className="h-px w-10 bg-[#e62429]" />
          Sports. Events. Experiences.
        </motion.p>

        <h1 className="font-display uppercase text-white text-[15vw] sm:text-[12vw] lg:text-[9.5vw] leading-[0.85]">
          <LineReveal lines={["Sometimes the", "brief is simple."]} delay={0.25} />
          <span className="block mt-1 text-[#e62429]">
            <LineReveal lines={["Sometimes it isn't."]} delay={0.5} />
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-14"
        >
          <p className="max-w-md text-base sm:text-lg text-neutral-300 leading-relaxed">
            From strategy to scale, we build meaningful experiences in sport,
            events and beyond. Whatever the ask — we design it, run it, grow it
            and make it matter.
          </p>
          <a
            href="#about"
            data-testid="hero-scroll-cta"
            className="group inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-white shrink-0"
          >
            <span className="flex h-12 w-12 items-center justify-center border border-white/30 group-hover:bg-[#e62429] group-hover:border-[#e62429] transition-colors duration-300">
              <ArrowDown className="h-5 w-5 group-hover:translate-y-0.5 transition-transform" />
            </span>
            Scroll to explore
          </a>
        </motion.div>
      </div>
    </section>
  );
};
